import type { GoalCarePlan } from '@seen/core/models/care-plans/GoalCarePlan'
import type { Intervention } from '@seen/core/models/care-plans/intervention'
import { Button } from '@seen/ui'
import { IconDotsVertical } from '@tabler/icons-react'
import { type JSX, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router'
import { InterventionActions } from '#src/components/care-plan/interventions/InterventionActions.tsx'
import { InterventionSheet } from '#src/components/InterventionSheet.tsx'

export function InterventionDrawer({
  interventionsByGoalCarePlan,
  onClose,
}: {
  interventionsByGoalCarePlan: {
    goalCarePlan: GoalCarePlan
    interventions: Intervention[]
  }[]
  // Replaces the default close, a route-relative navigation back to the plan,
  // for hosts that show the drawer outside Portal's route tree.
  onClose?: () => void
}): JSX.Element | null {
  const navigate = useNavigate()
  const params = useParams()
  const interventionId = params.interventionId

  const { intervention, interventions } = useMemo(() => {
    const entry = interventionsByGoalCarePlan.find((i) =>
      i.interventions.some((i) => i.id === interventionId)
    )
    if (!entry) {
      return {}
    }
    return {
      intervention: entry.interventions.find((i) => i.id === interventionId),
      interventions: entry.interventions,
    }
  }, [interventionId, interventionsByGoalCarePlan])

  const handleClose = (): void => {
    if (onClose) {
      onClose()
      return
    }
    void navigate('..', { replace: true })
  }

  return (
    <InterventionSheet
      intervention={intervention}
      open={!!interventionId}
      onOpenChange={(state) => !state && handleClose()}
      headerActions={
        intervention &&
        interventions && (
          <InterventionActions
            intervention={intervention}
            interventions={interventions}
          >
            <Button variant="ghost" size="sm">
              <IconDotsVertical className="size-4" />
            </Button>
          </InterventionActions>
        )
      }
    />
  )
}
